// Admin UI for city landing pages — list, seed all 43 cities at once,
// AI-generate copy, edit blocks, publish/unpublish, delete.
import { createFileRoute } from "@tanstack/react-router";
import { AdminOnlyGuard } from "@/components/admin/AdminOnlyGuard";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  Sparkles,
  Pencil,
  Trash2,
  ExternalLink,
  Search,
  Eye,
  EyeOff,
  Rocket,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  listLandingPages,
  upsertLandingPage,
  deleteLandingPage,
  seedLandingPages,
  generateLandingPageContent,
  bulkPublishLandingPages,
} from "@/lib/landing-pages.functions";

export const Route = createFileRoute("/admin/landing-byer")({
  component: () => (<AdminOnlyGuard><AdminLandingByer /></AdminOnlyGuard>),
});

interface UspBlock { title: string; body: string }
interface FaqBlock { question: string; answer: string }
interface SectionBlock { heading: string; body: string }

interface FormState {
  id?: string;
  city_slug: string;
  city_name: string;
  intro: string;
  usps: UspBlock[];
  faqs: FaqBlock[];
  body_sections: SectionBlock[];
  distance_km: string;
  hero_image: string;
  seo_title: string;
  seo_description: string;
  status: "draft" | "published";
}

const emptyForm: FormState = {
  city_slug: "",
  city_name: "",
  intro: "",
  usps: [],
  faqs: [],
  body_sections: [],
  distance_km: "",
  hero_image: "",
  seo_title: "",
  seo_description: "",
  status: "draft",
};

function AdminLandingByer() {
  const qc = useQueryClient();
  const list = useServerFn(listLandingPages);
  const upsert = useServerFn(upsertLandingPage);
  const remove = useServerFn(deleteLandingPage);
  const seed = useServerFn(seedLandingPages);
  const generate = useServerFn(generateLandingPageContent);
  const bulkPublish = useServerFn(bulkPublishLandingPages);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "draft" | "published">("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-landing-byer"],
    queryFn: () => list(),
  });

  const filtered = useMemo(() => {
    let rows = data?.pages ?? [];
    if (statusFilter !== "all") rows = rows.filter((r) => r.status === statusFilter);
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter((r) => r.city_name.toLowerCase().includes(q) || r.city_slug.includes(q));
    return rows;
  }, [data, statusFilter, search]);

  const seedMutation = useMutation({
    mutationFn: () => seed(),
    onSuccess: (res) => {
      toast.success(res.inserted > 0 ? `${res.inserted} byer oprettet som kladder` : "Alle byer findes allerede");
      qc.invalidateQueries({ queryKey: ["admin-landing-byer"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const saveMutation = useMutation({
    mutationFn: (payload: FormState) =>
      upsert({
        data: {
          id: payload.id,
          city_slug: payload.city_slug.trim().toLowerCase(),
          city_name: payload.city_name.trim(),
          intro: payload.intro || null,
          usps: payload.usps.filter((u) => u.title && u.body),
          faqs: payload.faqs.filter((f) => f.question && f.answer),
          body_sections: payload.body_sections.filter((s) => s.heading && s.body),
          distance_km: payload.distance_km ? Number(payload.distance_km) : null,
          hero_image: payload.hero_image || null,
          seo_title: payload.seo_title || null,
          seo_description: payload.seo_description || null,
          status: payload.status,
        },
      }),
    onSuccess: () => {
      toast.success("Gemt");
      setDialogOpen(false);
      setForm(emptyForm);
      qc.invalidateQueries({ queryKey: ["admin-landing-byer"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => {
      toast.success("Slettet");
      qc.invalidateQueries({ queryKey: ["admin-landing-byer"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const generateMutation = useMutation({
    mutationFn: (id: string) => generate({ data: { id } }),
    onMutate: (id) => setGeneratingId(id),
    onSettled: () => setGeneratingId(null),
    onSuccess: () => {
      toast.success("AI-indhold genereret");
      qc.invalidateQueries({ queryKey: ["admin-landing-byer"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const bulkMutation = useMutation({
    mutationFn: () => bulkPublish({ data: { onlyDrafts: true, publish: true } }),
    onSuccess: (res) => {
      const { summary } = res;
      toast.success(
        `Bulk-launch færdig: ${summary.published}/${summary.total} udgivet · ${summary.generated} AI-genereret · ${summary.failed} fejlede`,
        { duration: 8000 },
      );
      if (summary.failed > 0) {
        const failedNames = res.results
          .filter((r) => r.error)
          .map((r) => `${r.city_name}: ${r.error}`)
          .slice(0, 5)
          .join("\n");
        toast.error(`Fejl på ${summary.failed} byer:\n${failedNames}`, { duration: 12000 });
      }
      qc.invalidateQueries({ queryKey: ["admin-landing-byer"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const togglePublish = useMutation({
    mutationFn: (row: any) =>
      upsert({
        data: {
          id: row.id,
          city_slug: row.city_slug,
          city_name: row.city_name,
          intro: row.intro,
          usps: row.usps ?? [],
          faqs: row.faqs ?? [],
          body_sections: row.body_sections ?? [],
          distance_km: row.distance_km,
          hero_image: row.hero_image,
          seo_title: row.seo_title,
          seo_description: row.seo_description,
          status: row.status === "published" ? "draft" : "published",
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-landing-byer"] }),
    onError: (err: Error) => toast.error(err.message),
  });

  const openEdit = (row: any) => {
    setForm({
      id: row.id,
      city_slug: row.city_slug,
      city_name: row.city_name,
      intro: row.intro ?? "",
      usps: Array.isArray(row.usps) ? row.usps : [],
      faqs: Array.isArray(row.faqs) ? row.faqs : [],
      body_sections: Array.isArray(row.body_sections) ? row.body_sections : [],
      distance_km: row.distance_km != null ? String(row.distance_km) : "",
      hero_image: row.hero_image ?? "",
      seo_title: row.seo_title ?? "",
      seo_description: row.seo_description ?? "",
      status: row.status,
    });
    setDialogOpen(true);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Landing-byer</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            SEO-landingsider per by — fx /bilforhandler-kolding
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => seedMutation.mutate()}
            disabled={seedMutation.isPending || bulkMutation.isPending}
          >
            {seedMutation.isPending ? "Opretter…" : "Seed alle 43 byer"}
          </Button>

          <AlertDialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                disabled={bulkMutation.isPending}
                className="border-accent/40 text-accent hover:bg-accent/5"
              >
                <Rocket size={16} className="mr-2" />
                {bulkMutation.isPending ? "Launcher…" : "Bulk-launch alle byer"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Launch alle by-sider?</AlertDialogTitle>
                <AlertDialogDescription>
                  Dette vil for hver by uden indhold:
                  <br />1. Oprette en draft (hvis den ikke findes endnu)
                  <br />2. AI-generere SEO-tekst, USPs, FAQ og afsnit (~43 AI-kald)
                  <br />3. Sætte status til <strong>published</strong> så siden går live
                  <br /><br />
                  Allerede-publicerede sider med indhold genereres ikke om — de bliver bare publiceret igen (idempotent). Det tager 1-3 minutter. Fortsæt?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annullér</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => bulkMutation.mutate()}
                  className="bg-accent text-accent-foreground hover:opacity-90"
                >
                  Ja, launch alle
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Button
            onClick={() => { setForm(emptyForm); setDialogOpen(true); }}
            className="bg-accent text-accent-foreground hover:opacity-90"
          >
            <Plus size={16} className="mr-2" /> Ny by
          </Button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] max-w-md flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Søg by…"
            className="pl-9"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="all">Alle</option>
          <option value="draft">Kladder</option>
          <option value="published">Udgivet</option>
        </select>
        <span className="text-sm text-muted-foreground">{filtered.length} sider</span>
      </div>

      <div className="mt-4 overflow-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">By</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Slug</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Indhold</th>
              <th className="px-4 py-3 text-right font-medium text-muted-foreground">Handlinger</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Indlæser…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Ingen sider endnu — klik “Seed alle 43 byer”</td></tr>
            ) : (
              filtered.map((row: any) => {
                const hasContent = (row.intro && row.intro.length > 0) || (row.usps?.length ?? 0) > 0;
                return (
                  <tr key={row.id} className="border-t border-border hover:bg-muted/50">
                    <td className="px-4 py-3 font-medium text-foreground">{row.city_name}</td>
                    <td className="px-4 py-3 text-muted-foreground">/bilforhandler-{row.city_slug}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${row.status === "published" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                        {row.status === "published" ? "Udgivet" : "Kladde"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {hasContent ? "✓" : <span className="text-xs italic">Tom — generér med AI</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          title="Generér AI-indhold"
                          onClick={() => generateMutation.mutate(row.id)}
                          disabled={generatingId === row.id}
                          className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-accent disabled:opacity-50"
                        >
                          <Sparkles size={16} className={generatingId === row.id ? "animate-pulse" : ""} />
                        </button>
                        <button
                          title={row.status === "published" ? "Træk tilbage" : "Udgiv"}
                          onClick={() => togglePublish.mutate(row)}
                          className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                        >
                          {row.status === "published" ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                        {row.status === "published" && (
                          <a
                            title="Åbn side"
                            href={`/bilforhandler-${row.city_slug}`}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                          >
                            <ExternalLink size={16} />
                          </a>
                        )}
                        <button
                          title="Rediger"
                          onClick={() => openEdit(row)}
                          className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          title="Slet"
                          onClick={() => { if (confirm(`Slet ${row.city_name}?`)) deleteMutation.mutate(row.id); }}
                          className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-destructive"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.id ? `Rediger ${form.city_name}` : "Ny landing-by"}</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(form); }}
            className="space-y-4"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">By-navn *</label>
                <Input value={form.city_name} onChange={(e) => setForm({ ...form, city_name: e.target.value })} required />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Slug *</label>
                <Input
                  value={form.city_slug}
                  onChange={(e) => setForm({ ...form, city_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                  required
                  pattern="[a-z0-9-]+"
                  placeholder="kolding"
                />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Afstand (km)</label>
                <Input type="number" value={form.distance_km} onChange={(e) => setForm({ ...form, distance_km: e.target.value })} />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Status</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value as any })}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="draft">Kladde</option>
                  <option value="published">Udgivet</option>
                </select>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Hero-billede (URL)</label>
              <Input value={form.hero_image} onChange={(e) => setForm({ ...form, hero_image: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Intro</label>
              <Textarea rows={3} value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">SEO-titel</label>
                <Input value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} maxLength={70} />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">SEO-beskrivelse</label>
                <Input value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} maxLength={180} />
              </div>
            </div>

            <BlockEditor
              label="USP'er"
              fields={[{ key: "title", label: "Titel" }, { key: "body", label: "Tekst", textarea: true }]}
              items={form.usps as unknown as Array<Record<string, string>>}
              onChange={(items) => setForm({ ...form, usps: items as unknown as UspBlock[] })}
            />
            <BlockEditor
              label="Tekstafsnit"
              fields={[{ key: "heading", label: "Overskrift" }, { key: "body", label: "Tekst", textarea: true }]}
              items={form.body_sections as unknown as Array<Record<string, string>>}
              onChange={(items) => setForm({ ...form, body_sections: items as unknown as SectionBlock[] })}
            />
            <BlockEditor
              label="FAQ"
              fields={[{ key: "question", label: "Spørgsmål" }, { key: "answer", label: "Svar", textarea: true }]}
              items={form.faqs as unknown as Array<Record<string, string>>}
              onChange={(items) => setForm({ ...form, faqs: items as unknown as FaqBlock[] })}
            />

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuller</Button>
              <Button type="submit" disabled={saveMutation.isPending} className="bg-accent text-accent-foreground hover:opacity-90">
                {saveMutation.isPending ? "Gemmer…" : form.id ? "Opdater" : "Opret"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface BlockField { key: string; label: string; textarea?: boolean }

function BlockEditor({
  label,
  fields,
  items,
  onChange,
}: {
  label: string;
  fields: BlockField[];
  items: Array<Record<string, string>>;
  onChange: (items: Array<Record<string, string>>) => void;
}) {
  const addItem = () => onChange([...items, Object.fromEntries(fields.map((f) => [f.key, ""]))]);
  const updateItem = (idx: number, key: string, value: string) => {
    const next = [...items];
    next[idx] = { ...next[idx], [key]: value };
    onChange(next);
  };
  const removeItem = (idx: number) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div className="rounded-lg border border-border p-4">
      <div className="mb-3 flex items-center justify-between">
        <label className="text-sm font-semibold text-foreground">{label}</label>
        <Button type="button" size="sm" variant="outline" onClick={addItem}>
          <Plus size={14} className="mr-1" /> Tilføj
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">Ingen — klik "Tilføj" eller brug AI-knappen i listen.</p>
      ) : (
        <div className="space-y-3">
          {items.map((item, idx) => (
            <div key={idx} className="rounded-md border border-border bg-muted/30 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">#{idx + 1}</span>
                <button type="button" onClick={() => removeItem(idx)} className="text-muted-foreground hover:text-destructive">
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="space-y-2">
                {fields.map((f) => (
                  <div key={f.key}>
                    <label className="mb-1 block text-xs text-muted-foreground">{f.label}</label>
                    {f.textarea ? (
                      <Textarea
                        rows={3}
                        value={item[f.key] ?? ""}
                        onChange={(e) => updateItem(idx, f.key, e.target.value)}
                      />
                    ) : (
                      <Input
                        value={item[f.key] ?? ""}
                        onChange={(e) => updateItem(idx, f.key, e.target.value)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
